import React, { useMemo, useState } from 'react';
import { totalRowsLanded } from '../../hooks/runProgress';
import { useElapsed } from '../../hooks/useRunStream';
import { INGEST_STEPS, stepLabel, enrichStepLabel, overallProgress } from '../../lib/steps';

// The live readout of a running pipeline. A meter is a magnitude toward a known
// total, so it gets one hue (brand teal) on a recessive track — no categorical
// palette here. Step state is *status*, so it ships as icon + word, never colour
// alone.
const TEAL = '#00C6AE';
const NAVY = '#1B3A6B';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const secs = (ms) => (ms == null ? null : `${(ms / 1000).toFixed(1)}s`);

export function clock(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// `live` overlays a traveling sheen on the filled bar so an in-flight step whose
// count is momentarily static (progress lands per chunk) still reads as "working".
export function Meter({ value, max, tone = TEAL, height = 6, label, live = false }) {
  const pct = max > 0 ? Math.min(100, (100 * value) / max) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="w-full rounded-full overflow-hidden"
      style={{ height, background: '#e6e5df' }}
    >
      <div
        className="relative h-full rounded-full overflow-hidden transition-[width] duration-500 ease-out motion-reduce:transition-none"
        style={{ width: `${pct}%`, background: tone }}
      >
        {live && pct > 0 && (
          <span
            data-testid="live-shimmer"
            aria-hidden="true"
            className="sc-shimmer absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
              transform: 'translateX(-100%)'
            }}
          />
        )}
      </div>
    </div>
  );
}

// status → icon + word. Never colour alone.
export function StepIcon({ status }) {
  if (status === 'success') {
    return (
      <span className="flex-none w-5 h-5 rounded-full flex items-center justify-center" style={{ background: TEAL }}>
        <svg viewBox="0 0 20 20" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex-none w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
        <svg viewBox="0 0 20 20" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="flex-none w-5 h-5 rounded-full border-2 border-gray-200 flex items-center justify-center">
        <span
          className="w-2 h-2 rounded-full animate-pulse motion-reduce:animate-none"
          style={{ background: NAVY }}
        />
      </span>
    );
  }
  return <span className="flex-none w-5 h-5 rounded-full border-2 border-dashed border-gray-200" />;
}

function StepRow({ step }) {
  const { status } = step;
  const done = status === 'success';
  return (
    <li className="flex items-start gap-3 py-2">
      <StepIcon status={status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className={`text-sm ${status === 'pending' ? 'text-gray-400' : 'text-gray-900'}`}>
            {stepLabel(step.name)}
          </span>
          <span className="text-xs tabular-nums text-gray-500 flex-none">
            {status === 'pending' && 'Waiting'}
            {status === 'failed' && <span className="text-red-600 font-medium">Failed</span>}
            {status === 'running' &&
              (step.total ? `${fmt(step.done)} / ${fmt(step.total)}` : `${fmt(step.done)} rows`)}
            {done && `${fmt(step.rowsOut)} rows${secs(step.durationMs) ? ` · ${secs(step.durationMs)}` : ''}`}
          </span>
        </div>
        {status === 'running' && step.total > 0 && (
          <div className="mt-1.5">
            <Meter value={step.done} max={step.total} height={4} label={stepLabel(step.name)} />
            {step.throughput > 0 && (
              <p className="text-[11px] text-gray-400 mt-1 tabular-nums">
                {fmt(Math.round(step.throughput))} rows/sec
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * @param {object}  state       runProgress reducer state
 * @param {string}  kind        'ingest' | 'enrich' — picks the step manifest
 * @param {boolean} reattached  true when we joined a run already in flight
 * @param {string}  summary     what landed, shown on success
 */
export default function RunProgress({ state, kind = 'ingest', reattached = false, summary = '', onDismiss }) {
  const [showLog, setShowLog] = useState(false);
  const running = state.status === 'running';
  const elapsed = useElapsed(state.startedAt, running);

  const manifest = kind === 'ingest' ? INGEST_STEPS : null;
  const overall = useMemo(() => overallProgress(state, manifest), [state, manifest]);

  // Show the steps still to come, so the shape of the work is visible up front.
  const steps = useMemo(() => {
    if (!manifest || reattached) return state.steps;
    const seen = new Set(state.steps.map((s) => s.name));
    const pending = manifest.filter((n) => !seen.has(n)).map((name) => ({ name, status: 'pending' }));
    return [...state.steps, ...pending];
  }, [state.steps, manifest, reattached]);

  // What the run is working on: the live step, or — in the gap between two steps —
  // the one about to start.
  const activeStep = steps.find((s) => s.status === 'running') || steps.find((s) => s.status === 'pending');

  if (state.status === 'idle') return null;

  const noun = kind === 'ingest' ? 'Import' : 'Enrichment';
  const rows = totalRowsLanded(state);

  const heading =
    running ? `${noun === 'Import' ? 'Importing' : 'Enriching'} your data`
      : state.status === 'success' ? `${noun} complete`
      : `${noun} failed`;

  return (
    <section
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
      aria-live="polite"
      aria-busy={running}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">{heading}</h2>
          {running && (
            <p className="text-xs text-gray-500 mt-0.5">
              {overall && activeStep
                ? `Step ${overall.stepIndex} of ${overall.stepCount} · ${stepLabel(activeStep.name)}`
                : 'Working through the pipeline'}
            </p>
          )}
          {state.status === 'success' && (
            <p className="text-xs text-gray-500 mt-0.5">
              {summary ? `${summary} · ` : ''}
              {secs(state.durationMs) || `${clock(elapsed)}`}
            </p>
          )}
          {state.status === 'failed' && <p className="text-xs text-red-600 mt-0.5">{state.error}</p>}
        </div>
        <div className="flex items-center gap-3 flex-none">
          {running && (
            <span className="text-sm tabular-nums text-gray-500" aria-label="Elapsed time">
              {clock(elapsed)}
            </span>
          )}
          {!running && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-xs text-gray-500 hover:text-gray-900 underline underline-offset-2"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* A step can record a real error even though the run above reads
          "complete" — the header run's own status never flips over one step's
          failure (see runProgress.js's `done` case), because that would make the
          quality dashboard discard every OTHER step's real fills for the run.
          This is the only place that error reaches the screen at all, so it must
          name the step and show what it said, not just gesture at "something
          failed". */}
      {!running && state.failedSteps && state.failedSteps.length > 0 && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" role="alert">
          <p className="text-xs font-semibold" style={{ color: '#dc2626' }}>
            {state.failedSteps.length === 1
              ? "1 step didn't finish"
              : `${state.failedSteps.length} steps didn't finish`}
          </p>
          <ul className="mt-1.5 space-y-1">
            {state.failedSteps.map((s) => (
              <li key={s.name} className="text-xs" style={{ color: '#dc2626' }}>
                <span className="font-medium">{enrichStepLabel(s.name)}</span>: {s.err}
              </li>
            ))}
          </ul>
        </div>
      )}

      {overall && (
        <div className="mt-4">
          <Meter
            value={overall.fraction * 100}
            max={100}
            label="Overall import progress"
            tone={state.status === 'failed' ? '#dc2626' : TEAL}
          />
          <p className="text-xs text-gray-400 mt-1.5 tabular-nums">{Math.round(overall.fraction * 100)}%</p>
        </div>
      )}

      {reattached && running && (
        <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          Picked up an import that was already running. Steps before now aren&apos;t shown.
        </p>
      )}

      <ul className="mt-3 divide-y divide-gray-50">
        {steps.map((s) => (
          <StepRow key={s.name} step={s} />
        ))}
      </ul>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <p className="text-sm text-gray-600 tabular-nums">
          <span className="font-semibold text-gray-900">{fmt(rows)}</span> rows landed
        </p>
        <button
          type="button"
          onClick={() => setShowLog((v) => !v)}
          className="text-xs text-gray-500 hover:text-gray-900"
          aria-expanded={showLog}
        >
          {showLog ? 'Hide details' : 'Show details'}
        </button>
      </div>

      {showLog && (
        <pre className="mt-3 bg-gray-900 text-gray-100 rounded-lg p-3 max-h-56 overflow-y-auto font-mono text-[11px] leading-relaxed">
          {state.steps.length === 0
            ? 'Waiting for the first event…'
            : state.steps
                .map(
                  (s) =>
                    `${s.name.padEnd(16)} ${s.status.padEnd(8)} in=${s.rowsIn} out=${s.rowsOut}` +
                    `${s.durationMs != null ? ` ${s.durationMs}ms` : ''}`
                )
                .join('\n')}
          {state.error ? `\n\nERROR: ${state.error}` : ''}
        </pre>
      )}
    </section>
  );
}
